// ErpScreen.tsx - 기존 ERP(soonsoft) 실사용 150개 화면을 명세로 그리는 단일 화면.
//
// 구조는 기존 ERP 그대로다: 상단 = 모듈(기초·설비·…·금형), 좌측 = 그 모듈의 메뉴,
// 본문 = 검색조건 + 표. 사용자가 익힌 위치를 바꾸지 않는 것이 이관의 첫 조건이다.
// 150개를 메뉴로만 찾기는 어려워서 "화면 찾기" 검색을 둔다.
//
// 뷰마다 컴포넌트가 없다. 열·검색조건·편집 가능 여부는 전부 `GET /api/erp/meta`.
//
// 열린 메뉴는 주소가 소유한다(`/erp/<모듈>/<메뉴>` - `lib/route.ts`). 이 화면은
// 주소를 읽고, 메뉴를 고르면 `onOpenView` 로 주소를 바꿀 뿐 자기 상태를 두지 않는다.
import { ErpRecordForm, RefSelect } from "@/components/ErpRecordForm";
import { ErpTable } from "@/components/ErpTable";
import type { ScreenId } from "@/components/Shell";
import {
  Button,
  Card,
  DataBoundary,
  DateField,
  EmptyState,
  FilterBar,
  Icon,
  Pagination,
  Select,
  TableSkeleton,
  Tabs,
  TextInput,
} from "@/components/ui";
import { type Me, runErpView } from "@/lib/api";
import {
  type ErpEntity,
  type ErpFilter,
  type ErpMeta,
  type ErpRow,
  type ErpView,
  canWrite,
  initialParams,
  resolveView,
  searchViews,
  toCsv,
} from "@/lib/erp";
import { fmt } from "@/lib/format";
import { useErpMeta, useErpView } from "@/lib/hooks";
import { isPlainClick, pathFor } from "@/lib/route";
import { useIsMobile } from "@/lib/useViewport";
import type { ScreenNav } from "@/screens/nav";
import { useEffect, useMemo, useState } from "react";

const LAST_VIEW_KEY = "jwpds_erp_view";

const FilterControl = ({
  f,
  params,
  set,
  years,
  onSubmit,
}: {
  f: ErpFilter;
  params: Record<string, string>;
  set: (k: string, v: string) => void;
  years: string[];
  onSubmit: () => void;
}) => {
  const box = (child: React.ReactNode, width = 168) => (
    <span className="inline-flex items-center gap-s2" style={{ minWidth: width }}>
      <span
        className="whitespace-nowrap text-caption font-semibold"
        style={{ color: "var(--text-secondary)" }}
      >
        {f.label}
      </span>
      {child}
    </span>
  );
  switch (f.type) {
    case "date_range":
      return box(
        <span className="inline-flex items-center gap-s1">
          <DateField
            value={params[`${f.key}_from`] ?? ""}
            onChange={(v) => set(`${f.key}_from`, v)}
            ariaLabel={`${f.label} 시작`}
          />
          <span style={{ color: "var(--text-quaternary)" }}>~</span>
          <DateField
            value={params[`${f.key}_to`] ?? ""}
            onChange={(v) => set(`${f.key}_to`, v)}
            ariaLabel={`${f.label} 끝`}
          />
        </span>,
        320
      );
    case "month":
      return box(
        <input
          type="month"
          value={params[f.key] ?? ""}
          onChange={(e) => set(f.key, e.target.value)}
          aria-label={f.label}
          className="h-control-md rounded-control px-s3 text-label"
          style={{
            background: "var(--surface-inset)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-strong)",
          }}
          data-num=""
        />
      );
    case "year":
      return box(
        <Select
          value={params[f.key] ?? ""}
          onChange={(v) => set(f.key, v)}
          options={years}
          ariaLabel={f.label}
        />,
        140
      );
    case "enum":
      return box(
        <Select
          value={params[f.key] ?? ""}
          onChange={(v) => set(f.key, v)}
          options={f.options}
          placeholder="전체"
          ariaLabel={f.label}
        />
      );
    case "bool":
      return box(
        <Select
          value={params[f.key] ?? ""}
          onChange={(v) => set(f.key, v)}
          options={[
            { value: "1", label: "예" },
            { value: "0", label: "아니오" },
          ]}
          placeholder="전체"
          ariaLabel={f.label}
        />,
        140
      );
    case "ref":
      return f.ref
        ? box(
            <RefSelect
              entity={f.ref}
              value={params[f.key] ?? ""}
              onChange={(v) => set(f.key, v)}
              placeholder="전체"
              ariaLabel={f.label}
            />,
            220
          )
        : null;
    default:
      return box(
        <span
          className="inline-flex"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        >
          <TextInput
            value={params[f.key] ?? ""}
            onChange={(v) => set(f.key, v)}
            type="search"
            ariaLabel={f.label}
          />
        </span>,
        220
      );
  }
};

function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const ViewPanel = ({
  view,
  meta,
  nav,
  isAdmin,
}: {
  view: ErpView;
  meta: ErpMeta;
  nav: ScreenNav;
  isAdmin: boolean;
}) => {
  const entity: ErpEntity | null = view.entity ? (meta.entities[view.entity] ?? null) : null;
  const [draft, setDraft] = useState(() => initialParams(view));
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ErpRow | null | undefined>(undefined); // undefined = 닫힘, null = 새로
  const [exporting, setExporting] = useState(false);
  const pageSize = view.pageSize || 0;
  const res = useErpView(view.kind === "link" ? null : view.key, applied, page, pageSize);
  const years = useMemo(() => {
    const y = Number.parseInt(meta.asOf.slice(0, 4), 10) || new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => String(y + 1 - i));
  }, [meta.asOf]);

  const writable = canWrite(entity, isAdmin);
  const can = (a: string) => view.actions.includes(a as never);
  const submit = () => {
    setPage(1);
    setApplied({ ...draft });
  };

  if (view.kind === "link") {
    return (
      <Card>
        <EmptyState
          icon="external"
          title={`${view.title}은(는) PDS 개발품 화면에서 관리합니다`}
          description="기존 ERP 의 개발품 등록·진행 현황은 이 시스템의 개발품 상세관리(마일스톤·준비항목·지연 판정)가 대체합니다."
          action={
            <Button
              variant="primary"
              size="md"
              onClick={() => nav.goto(view.link as ScreenId)}
              iconEnd={<Icon name="arrowRight" size={14} />}
            >
              개발품 화면으로
            </Button>
          }
        />
      </Card>
    );
  }

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: ErpRow[] = [];
      let total = 0;
      let first = await runErpView(view.key, applied, 0, 5000);
      rows.push(...first.rows);
      total = first.total;
      while (rows.length < total && first.rows.length) {
        first = await runErpView(view.key, applied, rows.length, 5000);
        rows.push(...first.rows);
      }
      download(`${view.title}_${meta.asOf}.csv`, toCsv(first.columns, rows, first.totals));
    } catch (e) {
      nav.toast((e as Error).message, "error");
    } finally {
      setExporting(false);
    }
  };

  const data = res.data;
  const pageCount = data && pageSize ? Math.ceil(data.total / pageSize) : 1;

  return (
    <div className="flex min-w-0 flex-col gap-s3">
      <div className="flex flex-wrap items-end justify-between gap-s3">
        <div className="min-w-0">
          <h2 className="text-heading">{view.title}</h2>
          {view.notes.map((n) => (
            <p key={n} className="text-caption" style={{ color: "var(--text-tertiary)" }}>
              {n}
            </p>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-s2 print:hidden">
          {can("create") && writable && (
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => setEditing(null)}
            >
              추가
            </Button>
          )}
          {can("export") && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="download" size={14} />}
              loading={exporting}
              onClick={() => void exportCsv()}
            >
              엑셀(CSV)
            </Button>
          )}
          {can("print") && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="file" size={14} />}
              onClick={() => window.print()}
            >
              인쇄
            </Button>
          )}
        </div>
      </div>

      <Card pad="none">
        {view.filters.length > 0 && (
          <FilterBar
            right={
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="search" size={14} />}
                onClick={submit}
              >
                조회
              </Button>
            }
          >
            {view.filters.map((f) => (
              <FilterControl
                key={f.key}
                f={f}
                params={draft}
                years={years}
                set={(k, v) => setDraft((d) => ({ ...d, [k]: v }))}
                onSubmit={submit}
              />
            ))}
          </FilterBar>
        )}
        <DataBoundary
          loading={res.loading && !data}
          error={res.error}
          onRetry={() => void res.reload()}
          skeleton={<TableSkeleton />}
        >
          {data && data.rows.length === 0 ? (
            <EmptyState
              title="조회된 자료가 없습니다"
              description="검색조건을 바꿔 다시 조회해 보세요."
              icon="search"
            />
          ) : data ? (
            <ErpTable
              label={view.title}
              columns={data.columns}
              rows={data.rows}
              totals={data.totals}
              rowKey={(r, i) => (typeof r.id === "string" ? r.id : `${page}-${i}`)}
              onRowClick={entity && can("update") && writable ? (r) => setEditing(r) : undefined}
            />
          ) : null}
        </DataBoundary>
        {data && (
          <div className="flex items-center justify-between gap-s3 px-s4 py-s3 print:hidden">
            <span className="text-caption tabular-nums" style={{ color: "var(--text-tertiary)" }}>
              총 {fmt(data.total)}건
            </span>
            <Pagination page={page} pageCount={pageCount} onChange={setPage} />
            <span />
          </div>
        )}
      </Card>

      {entity && editing !== undefined && (
        <ErpRecordForm
          // 행마다 폼 상태를 새로 - 이전 행 입력이 남지 않게
          key={editing && typeof editing.id === "string" ? editing.id : "new"}
          open
          entity={entity}
          row={editing}
          canDelete={can("delete") && writable}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void res.reload();
          }}
          toast={nav.toast}
        />
      )}
    </div>
  );
};

export const ErpScreen = ({
  nav,
  me,
  viewKey: wanted,
  onOpenView,
}: {
  nav: ScreenNav;
  me: Me | null;
  /** 주소가 가리키는 뷰 키 (`/erp` 만이면 없음) */
  viewKey?: string;
  /** 메뉴 열기 = 주소 바꾸기. `replace` 는 주소를 바로잡을 때 (기록을 쌓지 않는다). */
  onOpenView: (key: string, replace?: boolean) => void;
}) => {
  const meta = useErpMeta();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");

  // 주소 → 마지막으로 본 메뉴 → 첫 메뉴. 매일 같은 2~3개 화면을 쓰는 업무라
  // `/erp` 로 들어오면 마지막 메뉴를 연다.
  const picked = useMemo(
    () =>
      meta.data
        ? resolveView(meta.data.modules, wanted, window.localStorage.getItem(LAST_VIEW_KEY))
        : null,
    [meta.data, wanted]
  );

  useEffect(() => {
    if (!picked) return;
    if (picked.view.key === wanted) {
      window.localStorage.setItem(LAST_VIEW_KEY, picked.view.key);
      return;
    }
    if (picked.missing) nav.toast("없는 ERP 메뉴라 다른 메뉴를 열었어요.", "error");
    onOpenView(picked.view.key, true);
  }, [picked, wanted, nav, onOpenView]);

  const open = (v: string) => {
    setQ("");
    onOpenView(v);
  };

  const hits = useMemo(
    () => (meta.data ? searchViews(meta.data.modules, q).slice(0, 12) : []),
    [meta.data, q]
  );
  const mod = picked?.module;
  const view = picked?.view;

  return (
    <DataBoundary loading={meta.loading} error={meta.error} onRetry={() => void meta.reload()}>
      {meta.data && (
        <div className="flex flex-col gap-s4">
          <div className="relative flex items-center gap-s3 print:hidden">
            <span className="w-full max-w-[420px]">
              <TextInput
                value={q}
                onChange={setQ}
                type="search"
                placeholder="화면 찾기 (예: 납품, 금형 현황)"
                ariaLabel="화면 찾기"
              />
            </span>
            {hits.length > 0 && (
              <div
                className="jw-overlay-material absolute left-0 top-full z-popover mt-s1 flex w-full max-w-[420px] flex-col rounded-control p-s1"
                style={{ boxShadow: "var(--shadow-overlay)" }}
              >
                {hits.map((h) => (
                  <button
                    key={h.view.key}
                    type="button"
                    onClick={() => open(h.view.key)}
                    className="flex items-center justify-between rounded-control px-s3 py-s2 text-left text-label hover:bg-overlay-hover"
                  >
                    <span>{h.view.title}</span>
                    <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                      {h.module.title}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="print:hidden">
            <Tabs
              label="ERP 모듈"
              items={meta.data.modules.map((m) => ({ key: m.key, label: m.title }))}
              active={mod?.key ?? ""}
              onChange={(k) => {
                const first = meta.data?.modules.find((x) => x.key === k)?.views[0];
                if (first) open(first.key);
              }}
            />
          </div>

          {mod && view ? (
            <div
              className={isMobile ? "flex flex-col gap-s3" : "grid gap-s4"}
              style={isMobile ? undefined : { gridTemplateColumns: "208px minmax(0, 1fr)" }}
            >
              {isMobile ? (
                <Select
                  value={view.key}
                  onChange={(v) => open(v)}
                  options={mod.views.map((v) => ({ value: v.key, label: v.title }))}
                  ariaLabel={`${mod.title} 메뉴`}
                />
              ) : (
                <nav aria-label={`${mod.title} 메뉴`} className="flex flex-col gap-px print:hidden">
                  {mod.views.map((v) => {
                    const on = v.key === view.key;
                    // 링크라서 새 탭으로 열기·주소 복사가 된다
                    return (
                      <a
                        key={v.key}
                        href={pathFor({ screen: "erp", erpView: v.key })}
                        aria-current={on ? "page" : undefined}
                        onClick={(e) => {
                          if (!isPlainClick(e)) return;
                          e.preventDefault();
                          open(v.key);
                        }}
                        className="block rounded-control px-s3 py-s2 text-left text-label no-underline transition-colors duration-fast ease-standard hover:bg-overlay-hover"
                        style={{
                          color: on ? "var(--accent)" : "var(--text-secondary)",
                          background: on ? "var(--accent-soft)" : undefined,
                          fontWeight: on ? 600 : 500,
                        }}
                      >
                        {v.title}
                      </a>
                    );
                  })}
                </nav>
              )}
              <ViewPanel
                key={view.key}
                view={view}
                meta={meta.data}
                nav={nav}
                isAdmin={me?.role === "admin"}
              />
            </div>
          ) : (
            <Card>
              <EmptyState title="이관된 화면이 없습니다" />
            </Card>
          )}
        </div>
      )}
    </DataBoundary>
  );
};
